import request from './request'

export type ProblemDifficulty = 1 | 2 | 3

// 题目样例
export interface Example {
  input: string
  output: string
  explanation?: string
}

export interface Problem {
  id: string
  problem_no?: string
  title: string
  description: string
  difficulty: ProblemDifficulty
  time_limit: number
  memory_limit: number
  sample_input: string
  sample_output: string
  examples?: Example[]
  tags: string[]
  created_at: string
  updated_at: string
  // 可选字段
  is_solved?: boolean
  status?: 'solved' | 'attempted' | 'unsolved'
  submit_count?: number      // 提交次数
  accepted_count?: number    // 通过次数
  pass_rate?: string         // 通过率（如"68.5%"）
  last_code?: string
}

export interface ProblemListRequest {
  page?: number
  page_size?: number
  difficulty?: ProblemDifficulty
  keyword?: string
  tags?: string[]
}

export interface ProblemListResponse {
  list: Problem[]
  total: number
  page: number
  page_size: number
}



export interface Tag {
  id: number
  name: string
  emoji: string
  color: string
  sort_order: number
}

export interface TagListResponse {
  tags: Tag[]
}

export interface HotTag {
  id: number
  name: string
  emoji: string
  color: string
  sort_order: number
  count: number  // 关联题目数量
}

export interface HotTagListResponse {
  tags: HotTag[]
}

// 获取题目列表
export function getProblemList(params: ProblemListRequest) {
  return request.get<ProblemListResponse>('/problems', { params })
}

// 获取题目详情
export function getProblemDetail(id: string) {
  return request.get<Problem>(`/problems/${id}`)
}

// 获取标签列表
export function getTagList() {
  return request.get<TagListResponse>('/tags')
}

// 获取热门标签（按题目数量统计前20）
export function getHotTagList() {
  return request.get<HotTagListResponse>('/tags/hot')
}

// 难度标签
export function getDifficultyType(difficulty?: ProblemDifficulty): 'success' | 'warning' | 'danger' | 'info' {
  const map: Record<ProblemDifficulty, 'success' | 'warning' | 'danger'> = {
    1: 'success',
    2: 'warning',
    3: 'danger'
  }
  return map[difficulty || 1] || 'info'
}

export function getDifficultyLabel(difficulty?: ProblemDifficulty) {
  const map: Record<ProblemDifficulty, string> = {
    1: '简单',
    2: '中等',
    3: '困难'
  }
  return map[difficulty || 1] || difficulty
}

// 提交代码
export interface SubmitCodeRequest {
  problem_id: string
  language: string
  code: string
}

export interface SubmitCodeResponse {
  submission_id: string
}

export function submitCode(data: SubmitCodeRequest) {
  return request.post<SubmitCodeResponse>('/submissions', data)
}

// 运行代码（自测）
export interface RunCodeRequest {
  problem_id: string
  language: string
  code: string
  input: string
}

export interface RunCodeResponse {
  status?: JudgeStatus  // 统一评测状态
  message?: string  // 提示消息
  output?: string   // 输出内容（结束时有）
  runtime?: number  // 运行时间(ms)（结束时有）
  memory?: number   // 内存使用(bytes)（结束时有）
  submission_id?: string  // 提交ID（提交时有）
  score?: number    // 得分（提交时有）
}

export interface SubmitCodeRequest {
  problem_id: string
  code: string
  language: string
  is_submit: boolean  // true=提交代码，false=测试运行
  input?: string      // 测试运行时的输入数据
}

// SSE 流式运行/提交代码（统一接口）
export function runCodeStream(
  data: SubmitCodeRequest,
  onMessage: (data: RunCodeResponse) => void,
  onError?: (error: Error) => void,
  onComplete?: () => void
) {
  return request.ssePost<RunCodeResponse>('/submissions/run/stream', data, {
    onMessage,
    onError,
    onComplete
  })
}

// 统一评测状态（流式中间状态 + 最终评测结果状态）
// 沙箱原始状态映射：
// Accepted -> accepted, Wrong Answer -> wrong_answer
// Time/Memory/Output Limit Exceeded -> 对应状态
// Non Zero Exit Status, Signalled -> runtime_error
// File Error, Dangerous Syscall, Internal Error -> system_error
export type JudgeStatus =
  // ========== 流式中间状态 ==========
  | 'pending'       // 等待中/初始状态
  | 'compiling'     // 编译中
  | 'running'       // 运行中
  // ========== 最终评测结果状态 ==========
  | 'accepted'              // 通过
  | 'wrong_answer'          // 答案错误
  | 'time_limit_exceeded'   // 超时
  | 'memory_limit_exceeded' // 超内存
  | 'output_limit_exceeded' // 输出超限
  | 'runtime_error'         // 运行错误
  | 'compile_error'         // 编译错误
  | 'system_error'          // 系统错误

// 提交记录
export interface Submission {
  id: string
  problem_id: string
  language: string
  status: JudgeStatus
  score: number
  runtime: number
  memory: number
  created_at: string
  // 详情字段
  code?: string
  error_info?: string
  message?: string
}

export interface SubmissionListResponse {
  list: Submission[]
  has_more: boolean
  next_cursor: number
}

export function getProblemSubmissions(problemId: string, cursor?: number, pageSize = 10) {
  const params: Record<string, any> = { problem_id: problemId, page_size: pageSize }
  if (cursor) {
    params.cursor = cursor
  }
  return request.get<SubmissionListResponse>('/submissions', { params })
}
